// raw-send-gate.ts — #333 Baileys-in-Chrome, slice 4.
//
// The ONE predicate that decides whether a send goes through the shared Baileys
// send core (`buildAndRelay` injected into WA Web's own socket + Signal store)
// instead of wa-js's `sendMessage`. Three layers, all default OFF, ANDed
// (plan §A7):
//
//   1. build-time kill   `RAW_SEND_ENABLED` — false ⇒ the raw branch is dead code in
//                        every customer build; flipping it is a CWS release.
//   2. per-user server   `userMessage.sendEngine === 'raw'` — set by the backend on
//                        the usermessage doc (same channel as `minEngineVersion`);
//                        the per-account rollout / instant server-side kill.
//   3. per-device local  `localStorage['bt-raw-send'] === 'true'` — the rig and the
//                        operator's own browser only; never set by product code.
//
// Plus the shape guards for what the v1 raw builder actually implements: a bare
// text (`conversation`) to a 1:1 chat. Quotes, link previews, mentions, groups,
// newsletters, status and broadcast lists stay native (groups follow once the
// 1:1 rig envelope passes — fork #37 already carries the sender-key path).
//
// Pure: no WA Web, no window. `readLocalRawSendOverride` is the only I/O and is
// injected so the predicate stays a truth table under test.

import type { WaMessage } from '@bt-common/interfaces/wa-store-types';

/** Layer 1. Same shape as `PAIRING_CODE_ENABLED` in ConnectGatewayDialog. */
export const RAW_SEND_ENABLED = false;

/** Layer 3 key. Same family as `bt-debug`. */
export const RAW_SEND_LOCAL_STORAGE_KEY = 'bt-raw-send';

/** Which engine carried the send — stamped on the trace (`@engine:*`). */
export type SendEngine = 'native' | 'raw' | 'raw-fallback';

export type RawSendGateInput = {
  waMessage: Pick<WaMessage, 'type' | 'chatId' | 'content' | 'options'>;
  /** `userMessage.sendEngine`, read as an optional local field (no bt-common type change). */
  sendEngine?: unknown;
  /** The resolved chat id (`chat.id._serialized`): a lid chat answers `@lid` here even for a `@c.us` target. */
  resolvedChatId?: string;
  localOverride?: boolean;
  buildFlag?: boolean;
};

/** Layer 3, read defensively: a blocked/absent localStorage reads as off. */
export function readLocalRawSendOverride(storage: Pick<Storage, 'getItem'> | undefined = safeLocalStorage()): boolean {
  try {
    return storage?.getItem(RAW_SEND_LOCAL_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function safeLocalStorage(): Pick<Storage, 'getItem'> | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

const USER_JID = /@(c\.us|s\.whatsapp\.net|lid)$/;

/** What the v1 raw builder sends: a plain text to a user chat, nothing attached. */
export function isRawSendableShape(
  waMessage: RawSendGateInput['waMessage'],
  resolvedChatId: string | undefined,
): boolean {
  const type = waMessage.type ?? 'text';
  if (type !== 'text') return false;
  if (typeof waMessage.content !== 'string' || waMessage.content.length === 0) return false;
  const o = waMessage.options;
  if (o?.quotedMessageId || o?.linkPreview || o?.attachment || o?.media) return false;
  // mentions / buttons / lists ride on options too; only the plain shape qualifies
  if (o && Object.keys(o).some((k) => !['body', 'sendMediaAsHd'].includes(k) && (o as Record<string, unknown>)[k] !== undefined)) {
    return false;
  }
  const target = String(resolvedChatId ?? waMessage.chatId ?? '').trim();
  return USER_JID.test(target);
}

/** The gate. True only when every layer and the shape guard agree. */
export function useRawSend(input: RawSendGateInput): boolean {
  const buildFlag = input.buildFlag ?? RAW_SEND_ENABLED;
  if (!buildFlag) return false;
  const serverFlag = input.sendEngine === 'raw';
  const localFlag = input.localOverride ?? readLocalRawSendOverride();
  if (!serverFlag && !localFlag) return false;
  return isRawSendableShape(input.waMessage, input.resolvedChatId);
}
